import { useCallback, useRef, useState } from 'react';
import { concatFloat32, downsample, encodeWav, rms } from '@frisk/shared';

/**
 * Records the microphone as raw PCM and hands back a 16 kHz mono WAV.
 *
 * An AudioWorklet rather than MediaRecorder so the format is identical in
 * Safari and Chrome and the server needs no ffmpeg. The worklet is inlined as
 * a Blob URL to keep it out of the build config.
 */
const WORKLET_SOURCE = `
class PcmCollector extends AudioWorkletProcessor {
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (channel && channel.length) this.port.postMessage(new Float32Array(channel));
    return true;
  }
}
registerProcessor('pcm-collector', PcmCollector);
`;

export type RecorderState = 'idle' | 'starting' | 'recording' | 'encoding' | 'error';

export interface VoiceRecorder {
  state: RecorderState;
  /** 0..1, drives the level bars. */
  level: number;
  error: string | null;
  start: () => Promise<void>;
  stop: () => Promise<Blob | null>;
  cancel: () => void;
}

export function useVoiceRecorder(): VoiceRecorder {
  const [state, setState] = useState<RecorderState>('idle');
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const chunks = useRef<Float32Array[]>([]);
  const context = useRef<AudioContext | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const workletUrl = useRef<string | null>(null);

  const teardown = useCallback(() => {
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    void context.current?.close().catch(() => {});
    context.current = null;
    if (workletUrl.current) URL.revokeObjectURL(workletUrl.current);
    workletUrl.current = null;
    setLevel(0);
  }, []);

  const start = useCallback(async () => {
    setError(null);
    setState('starting');
    chunks.current = [];

    try {
      const media = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
      stream.current = media;

      const ctx = new AudioContext();
      context.current = ctx;

      const url = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }));
      workletUrl.current = url;
      await ctx.audioWorklet.addModule(url);

      const node = new AudioWorkletNode(ctx, 'pcm-collector');
      node.port.onmessage = (event: MessageEvent<Float32Array>) => {
        chunks.current.push(event.data);
        setLevel(Math.min(1, rms(event.data) * 6));
      };
      ctx.createMediaStreamSource(media).connect(node);
      // Worklets need a destination to be pulled, but we must not echo the mic.
      node.connect(ctx.createGain()).connect(ctx.destination);

      setState('recording');
    } catch (err) {
      teardown();
      setState('error');
      setError(
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? 'Mikrofon-Zugriff verweigert'
          : 'Mikrofon nicht verfügbar',
      );
    }
  }, [teardown]);

  const stop = useCallback(async (): Promise<Blob | null> => {
    if (state !== 'recording' || !context.current) {
      teardown();
      setState('idle');
      return null;
    }
    setState('encoding');
    const sampleRate = context.current.sampleRate;
    const collected = concatFloat32(chunks.current);
    teardown();
    setState('idle');

    if (collected.length === 0) return null;
    return new Blob([encodeWav(downsample(collected, sampleRate))], { type: 'audio/wav' });
  }, [state, teardown]);

  const cancel = useCallback(() => {
    chunks.current = [];
    teardown();
    setState('idle');
  }, [teardown]);

  return { state, level, error, start, stop, cancel };
}
